import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { AgGridReact } from '@ag-grid-community/react'
import { ModuleRegistry } from '@ag-grid-community/core'
import type {
  CellClickedEvent,
  ColDef,
  FirstDataRenderedEvent,
  GetContextMenuItemsParams,
  GridReadyEvent,
  ICellRendererParams,
  IServerSideDatasource,
  PaginationChangedEvent,
  SelectionChangedEvent,
  SortChangedEvent,
} from '@ag-grid-community/core'
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model'
import { Dropdown } from '@surecompanies/core_components'
import MoreOutlined from '@ant-design/icons/MoreOutlined'
import { relativeTimeString } from 'enrollment-platform/utils/relative-time-string'
import ThreeStateHeaderCheckbox from '../../../components/ThreeStateHeaderCheckbox'
import type { SelectionMode } from '../../../components/ThreeStateHeaderCheckbox'
import SelectionBanner from '../../../components/SelectionBanner'
import type { PlanLocationOverrideRuleResponseDto } from '../../../api/dto/plan-location-override-rules.dto'
import { applySortModel, DEFAULT_SORT } from '../../../utils/gridSortModel'
import { PlanLocationOverrideRuleStatus } from '../enums/plan-location-override-rule.enum'

ModuleRegistry.registerModules([ServerSideRowModelModule])

const DEFAULT_PAGE_SIZE = 20
const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

interface Props {
  datasource: IServerSideDatasource
  defaultPageSize?: number
  pageSizeOptions?: number[]
  initialPage?: number
  initialSortModel?: { colId: string; sort: 'asc' | 'desc' }
  selectionMode: SelectionMode
  selectedCount: number
  totalCount: number
  isMultiPage: boolean
  programmaticSelectRef: React.MutableRefObject<boolean>
  onRowView: (ruleId: string) => void
  onRowEdit?: (ruleId: string) => void
  onRowApprove?: (ruleId: string) => void
  onRowReject?: (ruleId: string) => void
  onRowRedraft?: (ruleId: string) => void
  onRowDelete?: (ruleId: string) => void
  onSelectionChanged?: (ids: string[]) => void
  onHeaderCheckboxClicked: () => void
  onSelectAll: () => void
  onClearSelection: () => void
  onPageChanged?: () => void
  onPageChange?: (page: number) => void
  onPageSizeChange?: (size: number) => void
  onSortChange?: (colId: string, sort: 'asc' | 'desc') => void
}

const DEFAULT_COL_DEF: ColDef = {
  sortable: false,
  filter: false,
  resizable: true,
}

const ROW_STYLE = { cursor: 'pointer' } as const

const getRowId = ({ data }: { data: PlanLocationOverrideRuleResponseDto }) =>
  data.id

interface ActionsCellRendererParams
  extends ICellRendererParams<PlanLocationOverrideRuleResponseDto> {
  onRowView: (ruleId: string) => void
  onRowEdit?: (ruleId: string) => void
  onRowApprove?: (ruleId: string) => void
  onRowReject?: (ruleId: string) => void
  onRowRedraft?: (ruleId: string) => void
  onRowDelete?: (ruleId: string) => void
}

const ActionsCellRenderer = ({
  data,
  onRowView,
  onRowEdit,
  onRowApprove,
  onRowReject,
  onRowRedraft,
  onRowDelete,
}: ActionsCellRendererParams) => {
  if (!data) return null

  const status = data.status as PlanLocationOverrideRuleStatus
  const statusItems = [
    ...(status !== PlanLocationOverrideRuleStatus.APPROVED
      ? [{ key: 'approve', label: 'Approve', disabled: !onRowApprove }]
      : []),
    ...(status !== PlanLocationOverrideRuleStatus.REJECTED
      ? [
          {
            key: 'reject',
            label: 'Reject',
            danger: true as const,
            disabled: !onRowReject,
          },
        ]
      : []),
    ...(status !== PlanLocationOverrideRuleStatus.DRAFT
      ? [{ key: 'redraft', label: 'Re-draft', disabled: !onRowRedraft }]
      : []),
  ]

  return (
    <Dropdown
      trigger={['click']}
      menu={{
        items: [
          { key: 'view', label: 'View' },
          { key: 'edit', label: 'Edit', disabled: !onRowEdit },
          ...statusItems,
          {
            key: 'delete',
            label: 'Delete',
            danger: true as const,
            disabled: !onRowDelete,
          },
        ],
        onClick: ({ key, domEvent }) => {
          domEvent.stopPropagation()
          if (key === 'view') onRowView(data.id)
          else if (key === 'edit') onRowEdit?.(data.id)
          else if (key === 'approve') onRowApprove?.(data.id)
          else if (key === 'reject') onRowReject?.(data.id)
          else if (key === 'redraft') onRowRedraft?.(data.id)
          else if (key === 'delete') onRowDelete?.(data.id)
        },
      }}
    >
      <a onClick={(e) => e.preventDefault()} aria-label="row actions">
        <MoreOutlined />
      </a>
    </Dropdown>
  )
}

const LocationOverrideRulesGrid = forwardRef<
  AgGridReact<PlanLocationOverrideRuleResponseDto>,
  Props
>(function LocationOverrideRulesGrid(
  {
    datasource,
    defaultPageSize = DEFAULT_PAGE_SIZE,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    initialPage = 1,
    initialSortModel,
    selectionMode,
    selectedCount,
    totalCount,
    isMultiPage,
    programmaticSelectRef,
    onRowView,
    onRowEdit,
    onRowApprove,
    onRowReject,
    onRowRedraft,
    onRowDelete,
    onSelectionChanged,
    onHeaderCheckboxClicked,
    onSelectAll,
    onClearSelection,
    onPageChanged,
    onPageChange,
    onPageSizeChange,
    onSortChange,
  },
  ref
) {
  const [grid, setGrid] =
    useState<GridReadyEvent<PlanLocationOverrideRuleResponseDto>>()
  const gridInitializedRef = useRef(false)
  const programmaticSortRef = useRef(false)

  useEffect(() => {
    if (!grid) return
    grid.api.setGridOption('serverSideDatasource', datasource)
    grid.api.refreshServerSide({ purge: true })
  }, [grid, datasource])

  // Sync grid to URL page when browser back/forward changes the page param after initial render.
  useEffect(() => {
    if (!grid || !gridInitializedRef.current) return
    grid.api.paginationGoToPage(initialPage - 1)
  }, [grid, initialPage])

  // Sync grid to URL sort when browser back/forward changes the sort param after initial render.
  // programmaticSortRef prevents handleSortChanged from calling onSortChange (which would reset
  // page to 1) when we are the ones initiating the sort change.
  useEffect(() => {
    if (!grid || !gridInitializedRef.current) return
    programmaticSortRef.current = true
    if (initialSortModel) {
      grid.api.applyColumnState({
        state: [{ colId: initialSortModel.colId, sort: initialSortModel.sort }],
        defaultState: { sort: null },
      })
    } else {
      grid.api.applyColumnState({ defaultState: { sort: null } })
    }
  }, [grid, initialSortModel])

  // Sync grid to URL pageSize when browser back/forward changes the pageSize param after initial render.
  useEffect(() => {
    if (!grid || !gridInitializedRef.current) return
    grid.api.setGridOption('paginationPageSize', defaultPageSize)
    grid.api.setGridOption('cacheBlockSize', defaultPageSize)
    grid.api.refreshServerSide({ purge: true })
  }, [grid, defaultPageSize])

  const handleGridReady = useCallback(
    (event: GridReadyEvent<PlanLocationOverrideRuleResponseDto>) => {
      setGrid(event)
    },
    []
  )

  const handleFirstDataRendered = useCallback(
    (event: FirstDataRenderedEvent<PlanLocationOverrideRuleResponseDto>) => {
      if (selectionMode === 'all') {
        programmaticSelectRef.current = true
        event.api.selectAll()
      }
      event.api.autoSizeAllColumns(false)
      if (initialPage > 1) {
        event.api.paginationGoToPage(initialPage - 1)
      }
      gridInitializedRef.current = true
    },
    [selectionMode, programmaticSelectRef, initialPage]
  )

  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent<PlanLocationOverrideRuleResponseDto>) => {
      if (!onSelectionChanged) return
      if (programmaticSelectRef.current) {
        programmaticSelectRef.current = false
        return
      }
      const ids: string[] = []
      event.api.forEachNode((node) => {
        if (node.isSelected() && node.data?.id) ids.push(node.data.id)
      })
      onSelectionChanged(ids)
    },
    [onSelectionChanged, programmaticSelectRef]
  )

  const handleCellClicked = useCallback(
    (event: CellClickedEvent<PlanLocationOverrideRuleResponseDto>) => {
      if (event.column.getColId() === 'actions') return
      const ruleId = event.data?.id
      if (!ruleId) return
      onRowView(ruleId)
    },
    [onRowView]
  )

  const getContextMenuItems = useCallback(
    (
      params: GetContextMenuItemsParams<PlanLocationOverrideRuleResponseDto>
    ) => {
      return [
        {
          name: 'Copy Cell',
          action: () => {
            const value = params.value
            if (value != null) {
              void navigator.clipboard.writeText(String(value))
            }
          },
        },
      ]
    },
    []
  )

  const handlePaginationChanged = useCallback(
    (event: PaginationChangedEvent<PlanLocationOverrideRuleResponseDto>) => {
      if (event.newPage) {
        onPageChanged?.()
        if (gridInitializedRef.current) {
          onPageChange?.(event.api.paginationGetCurrentPage() + 1)
        }
      }
      if (event.newPageSize && gridInitializedRef.current) {
        onPageSizeChange?.(event.api.paginationGetPageSize())
      }
    },
    [onPageChanged, onPageChange, onPageSizeChange]
  )

  const handleSortChanged = useCallback(
    (event: SortChangedEvent<PlanLocationOverrideRuleResponseDto>) => {
      if (!gridInitializedRef.current) return
      if (programmaticSortRef.current) {
        programmaticSortRef.current = false
        return
      }
      const colState = event.api.getColumnState()
      const sorted = colState.find((c) => c.sort != null)
      if (sorted?.colId && sorted.sort) {
        onSortChange?.(sorted.colId, sorted.sort as 'asc' | 'desc')
        return
      }
      onSortChange?.(DEFAULT_SORT.colId, DEFAULT_SORT.sort)
    },
    [onSortChange]
  )

  const headerComponentParams = useMemo(
    () => ({ selectionMode, onHeaderCheckboxClicked }),
    [selectionMode, onHeaderCheckboxClicked]
  )

  const columnDefs = useMemo<
    ColDef<PlanLocationOverrideRuleResponseDto>[]
  >(() => {
    const sortFor = (colId: string) => applySortModel(colId, initialSortModel)
    return [
      {
        colId: 'year',
        headerName: 'Year',
        valueGetter: (p) => p.data?.match.year,
        checkboxSelection: true,
        headerComponent: ThreeStateHeaderCheckbox,
        headerComponentParams,
        minWidth: 80,
        sortable: true,
        ...sortFor('year'),
      },
      {
        colId: 'state',
        headerName: 'State',
        valueGetter: (p) => p.data?.match.state,
        minWidth: 80,
        sortable: true,
        ...sortFor('state'),
      },
      {
        colId: 'county',
        headerName: 'County',
        valueGetter: (p) => p.data?.params.county,
        minWidth: 120,
        sortable: false,
      },
      {
        colId: 'zipCode',
        headerName: 'Zip Code',
        valueGetter: (p) => p.data?.match.zipCode,
        minWidth: 100,
        sortable: true,
        ...sortFor('zipCode'),
      },
      {
        colId: 'countyFipsCode',
        headerName: 'County FIPS Code',
        valueGetter: (p) => p.data?.match.countyFipsCode,
        minWidth: 130,
        sortable: true,
        ...sortFor('countyFipsCode'),
      },
      {
        colId: 'ratingArea',
        headerName: 'Rating Area',
        valueGetter: (p) => p.data?.match.ratingArea,
        minWidth: 110,
        sortable: false,
      },
      {
        colId: 'updatedAt',
        headerName: 'Updated',
        field: 'updatedAt',
        valueFormatter: (p) =>
          p.value ? relativeTimeString(p.value as string, 30) : '—',
        minWidth: 160,
        sortable: true,
        ...sortFor('updatedAt'),
      },
      {
        colId: 'updatedBy',
        headerName: 'Updated by',
        valueGetter: (p) =>
          p.data?.updatedByEmail ?? p.data?.createdByEmail ?? '—',
        minWidth: 220,
        width: 220,
        sortable: false,
      },
      {
        colId: 'actions',
        headerName: 'Actions',
        cellRenderer: ActionsCellRenderer,
        cellRendererParams: {
          onRowView,
          onRowEdit,
          onRowApprove,
          onRowReject,
          onRowRedraft,
          onRowDelete,
        },
        pinned: 'right',
        width: 80,
        minWidth: 80,
        sortable: false,
      },
    ]
  }, [
    initialSortModel,
    headerComponentParams,
    onRowView,
    onRowEdit,
    onRowApprove,
    onRowReject,
    onRowRedraft,
    onRowDelete,
  ])

  return (
    <>
      <SelectionBanner
        selectionMode={selectionMode}
        selectedCount={selectedCount}
        totalCount={totalCount}
        isMultiPage={isMultiPage}
        onSelectAll={onSelectAll}
        onClearSelection={onClearSelection}
      />
      <div className="ag-theme-alpine grid">
        <AgGridReact
          ref={ref}
          getRowId={getRowId}
          rowModelType="serverSide"
          columnDefs={columnDefs}
          defaultColDef={DEFAULT_COL_DEF}
          rowSelection="multiple"
          suppressRowClickSelection={true}
          suppressMultiSort={true}
          pagination={true}
          paginationPageSize={defaultPageSize}
          paginationPageSizeSelector={pageSizeOptions}
          cacheBlockSize={defaultPageSize}
          onGridReady={handleGridReady}
          onFirstDataRendered={handleFirstDataRendered}
          onSelectionChanged={handleSelectionChanged}
          onCellClicked={handleCellClicked}
          onPaginationChanged={handlePaginationChanged}
          onSortChanged={handleSortChanged}
          getContextMenuItems={getContextMenuItems}
          rowStyle={ROW_STYLE}
          overlayLoadingTemplate="Loading..."
          overlayNoRowsTemplate="No location overrides yet"
          domLayout="autoHeight"
          suppressColumnVirtualisation={true}
        />
      </div>
    </>
  )
})

export default LocationOverrideRulesGrid
